/**
 * WoodMart collection page — filters, toolbar, grid layout
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getSectionId() {
    return document.getElementById('product-grid')?.dataset.id || document.querySelector('[data-section-id]')?.dataset.sectionId;
  }

  function buildFilterUrl(searchParams) {
    const sectionId = getSectionId();
    const base = `${window.location.pathname}?section_id=${sectionId}`;
    return searchParams ? `${base}&${searchParams}` : base;
  }

  function syncProductGalleries() {
    if (window.EnvoCommerce?.initProductCardGalleries) {
      window.EnvoCommerce.initProductCardGalleries();
    }
  }

  function initPriceSliders(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-price-range]').forEach((wrap) => {
      if (wrap.dataset.priceInit) return;
      wrap.dataset.priceInit = '1';

      const minRange = wrap.querySelector('[data-price-min-range]');
      const maxRange = wrap.querySelector('[data-price-max-range]');
      const minField = wrap.querySelector('[id$="-GTE"]');
      const maxField = wrap.querySelector('[id$="-LTE"]');
      const fill = wrap.querySelector('[data-price-range-fill]');
      const minLabel = wrap.querySelector('[data-price-min-label]');
      const maxLabel = wrap.querySelector('[data-price-max-label]');
      const max = Number(maxRange?.max || 0);

      if (!minRange || !maxRange || !max) return;

      const formatMoney = (cents) => {
        if (window.EnvoCommerce && typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, window.Shopify?.theme?.moneyFormat || '${{amount}}');
        }
        return (cents / 100).toFixed(2);
      };

      const updateFill = () => {
        const minVal = Number(minRange.value);
        const maxVal = Number(maxRange.value);
        const left = (minVal / max) * 100;
        const right = 100 - (maxVal / max) * 100;
        if (fill) {
          fill.style.left = `${left}%`;
          fill.style.right = `${right}%`;
        }
        if (minField) minField.value = (minVal / 100).toFixed(2);
        if (maxField) maxField.value = (maxVal / 100).toFixed(2);
        if (minLabel) minLabel.textContent = formatMoney(minVal);
        if (maxLabel) maxLabel.textContent = formatMoney(maxVal);
      };

      minRange.addEventListener('input', () => {
        if (Number(minRange.value) > Number(maxRange.value)) {
          minRange.value = maxRange.value;
        }
        updateFill();
      });

      maxRange.addEventListener('input', () => {
        if (Number(maxRange.value) < Number(minRange.value)) {
          maxRange.value = minRange.value;
        }
        updateFill();
      });

      [minField, maxField].forEach((field) => {
        field?.addEventListener('change', () => {
          const minCents = Math.round(Number(String(minField?.value || 0).replace(/,/g, '')) * 100);
          const maxCents = Math.round(Number(String(maxField?.value || max / 100).replace(/,/g, '')) * 100);
          minRange.value = Math.max(0, Math.min(minCents, max));
          maxRange.value = Math.max(Number(minRange.value), Math.min(maxCents, max));
          updateFill();
        });
      });

      updateFill();
    });
  }

  function initWidgetCollapse(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-widget-toggle]').forEach((btn) => {
      if (btn.dataset.widgetInit) return;
      btn.dataset.widgetInit = '1';
      btn.addEventListener('click', () => {
        const widget = btn.closest('.wd-collection-widget');
        const body = widget?.querySelector('.wd-collection-widget__body');
        const open = widget.classList.toggle('wd-opened');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (body) {
          if (open) body.removeAttribute('hidden');
          else body.setAttribute('hidden', '');
        }
      });
    });
  }

  async function renderCollectionSection(searchParams, pushState) {
    const url = buildFilterUrl(searchParams);
    const page = document.querySelector('.wd-collection-page');
    page?.classList.add('is-loading');

    try {
      const res = await fetch(url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.getElementById('ProductGridContainer');
      const newFilters = doc.getElementById('CollectionFiltersContainer');
      const newCount = doc.getElementById('ProductCount');

      if (newGrid) {
        document.getElementById('ProductGridContainer').innerHTML = newGrid.innerHTML;
      }
      if (newFilters) {
        const container = document.getElementById('CollectionFiltersContainer');
        if (container) container.innerHTML = newFilters.innerHTML;
      }
      if (newCount) {
        const countEl = document.getElementById('ProductCount');
        if (countEl) {
          countEl.innerHTML = newCount.innerHTML;
          countEl.dataset.productCount = newCount.dataset.productCount || '';
        }
      }

      if (pushState !== false) {
        const browserUrl = searchParams ? `${window.location.pathname}?${searchParams}` : window.location.pathname;
        window.history.pushState({ searchParams }, '', browserUrl);
      }

      bindCollectionFilters();
      initPriceSliders();
      initWidgetCollapse();
      syncProductGalleries();
      initInfiniteScroll();

      // Close mobile filter drawer after apply
      document.querySelector('.wd-collection-sidebar')?.classList.remove('is-open');
      document.body.classList.remove('wd-filter-open');
    } catch (e) {
      console.error(e);
    } finally {
      page?.classList.remove('is-loading');
    }
  }

  function getCombinedSearchParams(extraForm) {
    const forms = [document.getElementById('FacetFiltersForm'), document.getElementById('wd-collection-sort-form'), extraForm].filter(Boolean);
    const params = new URLSearchParams();

    forms.forEach((form) => {
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    });

    const current = new URLSearchParams(window.location.search);
    ['per_page', 'grid_cols'].forEach((key) => {
      if (current.has(key)) params.set(key, current.get(key));
    });

    return params.toString();
  }

  function bindCollectionFilters() {
    const container = document.getElementById('CollectionFiltersContainer');
    if (!container) return;

    if (!container.dataset.bound) {
      container.dataset.bound = '1';

      const submitFilters = debounce(() => {
        renderCollectionSection(getCombinedSearchParams());
      }, 400);

      container.addEventListener('change', (e) => {
        if (!e.target.closest('#FacetFiltersForm')) return;
        if (e.target.matches('[data-price-min-range], [data-price-max-range]')) return;
        if (e.target.type === 'text') return;
        submitFilters();
      });

      container.addEventListener('submit', (e) => {
        if (!e.target.matches('#FacetFiltersForm')) return;
        e.preventDefault();
        renderCollectionSection(getCombinedSearchParams());
      });
    }

    document.querySelectorAll('[data-facet-remove]').forEach((link) => {
      if (link.dataset.facetBound) return;
      link.dataset.facetBound = '1';
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = new URL(link.href, window.location.origin);
        renderCollectionSection(target.search.slice(1));
      });
    });
  }

  function initToolbar() {
    const sortSelect = document.querySelector('[data-collection-sort]');
    sortSelect?.addEventListener('change', () => {
      document.querySelector('.wd-collection-toolbar__sort-wrap')?.classList.remove('is-open');
      renderCollectionSection(getCombinedSearchParams());
    });

    const sortToggle = document.querySelector('[data-sort-toggle]');
    const sortWrap = document.querySelector('.wd-collection-toolbar__sort-wrap');
    sortToggle?.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = sortWrap?.classList.toggle('is-open');
      sortToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.wd-collection-toolbar__sort-wrap')) {
        sortWrap?.classList.remove('is-open');
        sortToggle?.setAttribute('aria-expanded', 'false');
      }
    });

    document.querySelectorAll('[data-grid-cols]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const cols = btn.dataset.gridCols;
        const grids = document.querySelectorAll('.wd-collection-grid');
        if (!grids.length) return;

        document.querySelectorAll('[data-grid-cols]').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        grids.forEach((grid) => {
          grid.classList.remove('wd-grid-cols-3', 'wd-grid-cols-4', 'wd-grid-cols-5');
          grid.classList.add(`wd-grid-cols-${cols}`);
          grid.dataset.columns = cols;
        });

        const productGrid = document.getElementById('product-grid');
        if (productGrid) productGrid.dataset.columns = cols;

        const params = new URLSearchParams(window.location.search);
        params.set('grid_cols', cols);
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
      });
    });

    document.querySelectorAll('[data-per-page]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const perPage = link.dataset.perPage;
        const params = new URLSearchParams(window.location.search);
        params.set('per_page', perPage);
        params.delete('page');
        window.location.href = `${window.location.pathname}?${params.toString()}`;
      });
    });
  }

  function initMobileFilters() {
    const sidebar = document.querySelector('.wd-collection-sidebar');
    if (!sidebar) return;

    document.querySelector('[data-filter-open]')?.addEventListener('click', () => {
      sidebar.classList.add('is-open');
      document.body.classList.add('wd-filter-open');
    });

    sidebar.querySelectorAll('[data-filter-close]').forEach((el) => {
      el.addEventListener('click', () => {
        sidebar.classList.remove('is-open');
        document.body.classList.remove('wd-filter-open');
      });
    });
  }

  function initInfiniteScroll() {
    const container = document.querySelector('[data-infinite-scroll]');
    if (!container) return;

    if (window.__wdInfiniteObserver) {
      window.__wdInfiniteObserver.disconnect();
      window.__wdInfiniteObserver = null;
    }
    if (window.__wdInfiniteScrollHandler) {
      window.removeEventListener('scroll', window.__wdInfiniteScrollHandler);
      window.__wdInfiniteScrollHandler = null;
    }

    container.dataset.infiniteInit = '1';

    const sentinel = container.querySelector('[data-infinite-sentinel]');
    const loader = container.querySelector('[data-infinite-loader]');
    const metaEl = container.querySelector('[data-infinite-meta]');
    if (!sentinel || !metaEl) return;

    let isLoading = false;
    const groupByBrand = container.dataset.groupByBrand === 'true';

    function getNextUrl() {
      try {
        const data = JSON.parse(metaEl.textContent || '{}');
        return data.nextUrl || null;
      } catch {
        return null;
      }
    }

    function setNextUrl(url) {
      metaEl.textContent = JSON.stringify({ nextUrl: url || '' });
    }

    function adoptCard(card) {
      const node = document.importNode(card, true);
      return node;
    }

    function animateCard(card, index) {
      card.classList.add('wd-collection-card-enter');
      card.style.animationDelay = `${Math.min(index * 0.05, 0.45)}s`;
    }

    function appendSimpleGridProducts(doc) {
      const sourceGrid = doc.getElementById('product-grid');
      const destGrid = document.getElementById('product-grid');
      if (!sourceGrid || !destGrid) return 0;

      const cards = [...sourceGrid.querySelectorAll('.wd-product-card')];
      cards.forEach((card, index) => {
        const adopted = adoptCard(card);
        animateCard(adopted, index);
        destGrid.appendChild(adopted);
      });
      return cards.length;
    }

    function appendBrandGridProducts(doc) {
      const sourceRoot = doc.getElementById('product-grid');
      const destRoot = document.getElementById('product-grid');
      if (!sourceRoot || !destRoot) return 0;

      let cardIndex = 0;
      let added = 0;

      sourceRoot.querySelectorAll('.wd-collection-brand-group').forEach((sourceGroup) => {
        const brandId = sourceGroup.id;
        const sourceGrid = sourceGroup.querySelector('.wd-collection-brand-group__grid');
        if (!sourceGrid) return;

        const destGroup = brandId ? document.getElementById(brandId) : null;
        const cards = [...sourceGrid.querySelectorAll('.wd-product-card')];

        if (destGroup) {
          const destGrid = destGroup.querySelector('.wd-collection-brand-group__grid');
          if (!destGrid) return;
          cards.forEach((card) => {
            const adopted = adoptCard(card);
            animateCard(adopted, cardIndex);
            destGrid.appendChild(adopted);
            cardIndex += 1;
            added += 1;
          });
        } else if (cards.length) {
          const adoptedGroup = document.importNode(sourceGroup, true);
          adoptedGroup.querySelectorAll('.wd-product-card').forEach((card, index) => {
            animateCard(card, cardIndex + index);
          });
          destRoot.appendChild(adoptedGroup);
          cardIndex += cards.length;
          added += cards.length;
        }
      });

      return added;
    }

    function markComplete() {
      sentinel.remove();
      loader?.setAttribute('hidden', '');
      let endEl = container.querySelector('[data-infinite-end]');
      if (!endEl) {
        endEl = document.createElement('p');
        endEl.className = 'wd-collection-infinite__end';
        endEl.dataset.infiniteEnd = '';
        endEl.textContent = "You've seen all products";
        container.appendChild(endEl);
      }
      setNextUrl('');
      if (window.__wdInfiniteObserver) {
        window.__wdInfiniteObserver.disconnect();
        window.__wdInfiniteObserver = null;
      }
    }

    function updateInfiniteFromDoc(doc) {
      const newContainer = doc.querySelector('[data-infinite-scroll]');
      const newMeta = newContainer?.querySelector('[data-infinite-meta]');

      if (!newMeta) {
        markComplete();
        return;
      }

      try {
        const data = JSON.parse(newMeta.textContent || '{}');
        setNextUrl(data.nextUrl || '');
        if (!data.nextUrl) markComplete();
      } catch {
        setNextUrl('');
        markComplete();
      }
    }

    function isNearViewport() {
      const rect = sentinel.getBoundingClientRect();
      return rect.top <= window.innerHeight + 320;
    }

    function maybeLoadMore() {
      if (!getNextUrl() || isLoading) return;
      if (isNearViewport()) loadNextPage();
    }

    async function loadNextPage() {
      const nextPath = getNextUrl();
      if (!nextPath || isLoading) return;

      isLoading = true;
      loader?.removeAttribute('hidden');
      container.classList.add('is-loading-more');

      try {
        const nextUrl = new URL(nextPath, window.location.origin);
        const sectionId = getSectionId();
        if (sectionId) nextUrl.searchParams.set('section_id', sectionId);

        const res = await fetch(nextUrl.toString());
        if (!res.ok) throw new Error(`Failed to load products (${res.status})`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const added = groupByBrand
          ? appendBrandGridProducts(doc)
          : appendSimpleGridProducts(doc);

        if (added === 0) {
          console.warn('Infinite scroll: no products found in response');
        }

        updateInfiniteFromDoc(doc);
        syncProductGalleries();

        if (getNextUrl() && isNearViewport()) {
          requestAnimationFrame(() => maybeLoadMore());
        }
      } catch (e) {
        console.error('Infinite scroll error:', e);
      } finally {
        isLoading = false;
        loader?.setAttribute('hidden', '');
        container.classList.remove('is-loading-more');
      }
    }

    window.__wdInfiniteObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadNextPage();
        }
      },
      { root: null, rootMargin: '400px 0px 400px 0px', threshold: 0 }
    );

    window.__wdInfiniteObserver.observe(sentinel);

    window.__wdInfiniteScrollHandler = debounce(maybeLoadMore, 120);
    window.addEventListener('scroll', window.__wdInfiniteScrollHandler, { passive: true });

    requestAnimationFrame(() => {
      maybeLoadMore();
    });
  }

  function init() {
    if (!document.querySelector('.wd-collection-page')) return;

    bindCollectionFilters();
    initPriceSliders();
    initWidgetCollapse();
    initToolbar();
    initMobileFilters();
    initInfiniteScroll();
    syncProductGalleries();

    window.addEventListener('popstate', (event) => {
      renderCollectionSection(event.state?.searchParams || window.location.search.slice(1), false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
